import React from "react";
import { Settings } from "lucide-react";
import { fetchEpiSettings, refreshEpiModels, saveEpiSettings } from "../../lib/assistantApi.js";

const EFFORT_LABELS = {
  low: "Faible (low)", medium: "Moyen (medium)", high: "Élevé (high)",
  xhigh: "Très élevé (xhigh)", max: "Maximum (max)",
};
const CONTROL = "h-6 rounded-[4px] border border-slate-300 bg-white px-2 py-0 text-xs text-slate-900 focus-visible:outline-accent disabled:opacity-60";

// Les réglages restent derrière la roue dentée : ils ne prennent pas la place de la conversation.
export default function EpiSettings({ disabled = false }) {
  const rootRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const [selection, setSelection] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [saved, setSaved] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshed, setRefreshed] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  React.useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setError("");
    fetchEpiSettings().then((value) => {
      if (active) {
        setSettings(value);
        setSelection({ model: value.model, effort: value.effort });
      }
    }).catch((failure) => { if (active) setError(failure.message); });
    return () => { active = false; };
  }, [open, retry]);
  React.useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    window.addEventListener("pointerdown", closeOutside);
    return () => window.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  const selectedModel = settings?.models.find((item) => item.id === selection?.model);
  const changed = selection && (selection.model !== settings.model || selection.effort !== settings.effort);
  const busy = disabled || saving || refreshing;
  const validSelection = selectedModel?.efforts.includes(selection?.effort);
  const update = (patch) => {
    setSelection((current) => ({ ...current, ...patch }));
    setSaved(false);
    setError("");
  };
  const save = async (event) => {
    event.preventDefault();
    if (!changed || busy || !validSelection) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const value = await saveEpiSettings(selection);
      setSettings((current) => ({ ...current, ...value }));
      setSaved(true);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setSaving(false);
    }
  };
  const refresh = async () => {
    if (busy) return;
    setRefreshing(true);
    setRefreshed(false);
    setError("");
    try {
      const value = await refreshEpiModels();
      // L'actualisation ne touche ni au choix enregistré ni à la saisie en cours.
      setSettings((current) => ({ ...current, models: value.models }));
      setRefreshed(true);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div ref={rootRef}>
      <button aria-expanded={open} aria-label="Réglages administrateur" className="epi-icon" onClick={() => setOpen((value) => !value)} title="Réglages administrateur" type="button"><Settings aria-hidden="true" size={16} /></button>
      {open ? (
    <form aria-label="Réglages administrateur de Lumi" className="epi-settings" onSubmit={save}>
      <p className="epi-hint"><strong>Réglages administrateur</strong> · Par défaut pour tous les utilisateurs de cette compagnie.</p>
      {settings ? (
        <>
          <div className="epi-settings__controls">
            <label>Modèle Claude
              <select className={CONTROL} disabled={busy} value={selection.model} onChange={(event) => {
                const model = settings.models.find((item) => item.id === event.target.value);
                update({ model: model.id, effort: model.efforts.includes(selection.effort) ? selection.effort : model.efforts[0] });
              }}>
                {!selectedModel ? <option value={selection.model}>{selection.model} (indisponible)</option> : null}
                {settings.models.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>Effort de réflexion
              <select className={CONTROL} disabled={busy} value={selection.effort} onChange={(event) => update({ effort: event.target.value })}>
                {!validSelection ? <option value={selection.effort}>{EFFORT_LABELS[selection.effort] || selection.effort} (indisponible)</option> : null}
                {selectedModel?.efforts.map((effort) => <option key={effort} value={effort}>{EFFORT_LABELS[effort] || effort}</option>)}
              </select>
            </label>
            <button className="h-6 rounded-[4px] border border-accent bg-accent px-3 py-0 text-xs font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
              disabled={!changed || busy || !validSelection} type="submit">{saving ? "Enregistrement…" : "Enregistrer"}</button>
            <button className="h-6 rounded-[4px] border border-slate-300 bg-white px-3 py-0 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
              disabled={busy} onClick={refresh} type="button">{refreshing ? "Actualisation…" : "Actualiser les modèles"}</button>
          </div>
          {!validSelection ? <p className="epi-hint">Le choix sélectionné n’est plus proposé. Aucun remplacement automatique n’est effectué ; choisissez un modèle et un effort disponibles pour le modifier.</p> : null}
          <p className="epi-hint">Un effort plus faible réduit généralement le coût et le temps de réponse.</p>
          {settings.provider !== "claude_api" ? <p className="epi-hint">Ces réglages s’appliquent à l’API Claude. Ce serveur utilise actuellement un autre mode d’accès.</p> : null}
        </>
      ) : error ? <button className="epi-action" onClick={() => setRetry((value) => value + 1)} type="button">Réessayer</button>
        : <p className="epi-hint" role="status">Chargement des réglages…</p>}
      {error ? <p className="epi-error" role="alert">{error}</p> : null}
      {saved ? <p className="epi-hint" role="status">Réglages enregistrés pour les prochaines questions.</p> : null}
      {refreshed ? <p className="epi-hint" role="status">Liste des modèles actualisée. Votre choix par défaut est conservé.</p> : null}
    </form>
      ) : null}
    </div>
  );
}
